// API Configuration
import { API_BASE_URL } from './baseUrl';

// API Endpoints
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: `${API_BASE_URL}/auth/login`,
    REGISTER: `${API_BASE_URL}/auth/register`,
    ME: `${API_BASE_URL}/auth/me`,
    VERIFY_EMAIL: `${API_BASE_URL}/auth/verify-email`,
    RESEND_VERIFICATION: `${API_BASE_URL}/auth/resend-verification`,
  },
  TODOS: {
    CREATE: `${API_BASE_URL}/todos`,
    GET_ALL: `${API_BASE_URL}/todos`,
    UPDATE: `${API_BASE_URL}/todos`,
    DELETE: (id: string) => `${API_BASE_URL}/todos/${id}`,
  },
  HABITS: {
    GET_ALL: `${API_BASE_URL}/habits`,
    GET_WITH_COMPLETIONS: `${API_BASE_URL}/habits/with-completions`,
    GET_BY_ID: (id: string) => `${API_BASE_URL}/habits/${id}`,
    CREATE: `${API_BASE_URL}/habits`,
    UPDATE_BY_ID: (id: string) => `${API_BASE_URL}/habits/${id}`,
    DELETE_BY_ID: (id: string) => `${API_BASE_URL}/habits/${id}`,
    CREATE_COMPLETION: (habitId: string) => `${API_BASE_URL}/habits/${habitId}/completions`,
    GET_COMPLETIONS: (habitId: string) => `${API_BASE_URL}/habits/${habitId}/completions`,
  },
  NOTES: {
    GET_ALL: `${API_BASE_URL}/notes`,
    CREATE: `${API_BASE_URL}/notes`,
    UPDATE: (id: string) => `${API_BASE_URL}/notes/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/notes/${id}`,
    GET_PUBLIC: (id: string) => `${API_BASE_URL}/notes/public/${id}`,
    GET_BY_URLFRIENDLYNAME: (urlfriendlyname: string) => `${API_BASE_URL}/notes/url/${urlfriendlyname}`,
    SHARE: `${API_BASE_URL}/notes/share`,
  },
  USERS: {
    GET_PUBLIC_PROFILE: (userId: string) => `${API_BASE_URL}/profile/${userId}/public`,
  },
  REMINDERS: {
    GET_ALL: `${API_BASE_URL}/reminders`,
    CREATE: `${API_BASE_URL}/reminders`,
    UPDATE_BY_ID: (id: string) => `${API_BASE_URL}/reminders/${id}`,
    DELETE_BY_ID: (id: string) => `${API_BASE_URL}/reminders/${id}`,
  },
  TRACKERS: {
    GET_ALL: `${API_BASE_URL}/trackers`,
    GET_WITH_ENTRIES: `${API_BASE_URL}/trackers/with-entries`,
    GET_BY_ID: (id: string) => `${API_BASE_URL}/trackers/${id}`,
    GET_TRACKER_WITH_ENTRIES: (id: string) => `${API_BASE_URL}/trackers/${id}/with-entries`,
    CREATE: `${API_BASE_URL}/trackers`,
    UPDATE: `${API_BASE_URL}/trackers`,
    DELETE: `${API_BASE_URL}/trackers`,
    GET_ENTRIES_BY_TRACKER: (trackerId: string) => `${API_BASE_URL}/trackers/entries/${trackerId}`,
    ADD_ENTRY: `${API_BASE_URL}/trackers/entries`,
    UPDATE_ENTRY: `${API_BASE_URL}/trackers/entries`,
    DELETE_ENTRY: `${API_BASE_URL}/trackers/entries`,
  },
  JOURNAL: {
    GET_ALL: `${API_BASE_URL}/journal`,
    GET_BY_RANGE: `${API_BASE_URL}/journal/range`,
    GET_BY_ID: (id: string) => `${API_BASE_URL}/journal/${id}`,
    CREATE: `${API_BASE_URL}/journal`,
    UPDATE: (id: string) => `${API_BASE_URL}/journal/${id}`,
    DELETE: (id: string) => `${API_BASE_URL}/journal/${id}`,
  },
} as const;

// Helper function to get authenticated headers
export const getAuthHeaders = (additionalHeaders?: Record<string, string>) => {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };

  // Add authorization header if token exists
  const token = localStorage.getItem("accessToken");
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  return headers;
};

// Enhanced fetch wrapper that handles token expiration
export const authenticatedFetch = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  // Check if offline - allow requests to proceed for cached responses
  if (!navigator.onLine) {
    console.log('Offline mode: attempting cached request');
  }

  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  // If response is 401 (Unauthorized), token might be expired
  if (response.status === 401) {
    console.log("Received 401 response, token might be expired");
    // Import handleTokenExpiration dynamically to avoid circular dependencies
    const { handleTokenExpiration } = await import("../utils/auth");
    handleTokenExpiration();
    // Return the response so the calling code can handle it if needed
  }

  return response;
};
